import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, InputLabel, MenuItem, Select, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Typography, Paper
} from '@mui/material';
import { PayrollService } from '../../services/payroll.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import {
    IRecordBatchRequest, ISalaryPaymentBatch, SalaryPaymentMethod
} from '../../interfaces/salaryPayment';

export interface BatchPayrollRow {
    id: string;
    employeeName: string;
    netSalary: number;
    user?: {
        bankName?: string | null;
        bankAccountNumber?: string | null;
        mobileBankingProvider?: string | null;
        mobileBankingAccount?: string | null;
    };
}

interface Props {
    open: boolean;
    onClose: () => void;
    selectedPayrolls: BatchPayrollRow[];
    onSuccess: (batch: ISalaryPaymentBatch) => void;
}

const METHODS: SalaryPaymentMethod[] = ['CASH', 'BANK_TRANSFER', 'MOBILE_BANKING', 'CHEQUE', 'OTHER'];

const todayIso = () => new Date().toISOString().substring(0, 10);

const isMissing = (v?: string | null) => !v || v.trim() === '';

const BatchPaymentDialog: React.FC<Props> = ({ open, onClose, selectedPayrolls, onSuccess }) => {
    const { t } = useTranslation(['payroll', 'common']);
    const { showSuccess, showApiError } = useToast();
    const [batchDate, setBatchDate] = useState<string>(todayIso());
    const [paymentMethod, setPaymentMethod] = useState<SalaryPaymentMethod>('CASH');
    const [reference, setReference] = useState<string>('');
    const [notes, setNotes] = useState<string>('');
    const [reason, setReason] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setBatchDate(todayIso());
            setPaymentMethod('CASH');
            setReference('');
            setNotes('');
            setReason('');
        }
    }, [open]);

    const totalAmount = useMemo(
        () => selectedPayrolls.reduce((sum, p) => sum + (Number(p.netSalary) || 0), 0),
        [selectedPayrolls]
    );

    const missingInfoCount = useMemo(() => {
        if (paymentMethod === 'BANK_TRANSFER') {
            return selectedPayrolls.filter(p =>
                isMissing(p.user?.bankName) || isMissing(p.user?.bankAccountNumber)
            ).length;
        }
        if (paymentMethod === 'MOBILE_BANKING') {
            return selectedPayrolls.filter(p =>
                isMissing(p.user?.mobileBankingProvider) || isMissing(p.user?.mobileBankingAccount)
            ).length;
        }
        return 0;
    }, [selectedPayrolls, paymentMethod]);

    const bankingUnavailable = selectedPayrolls.some(p => p.user === undefined)
        && (paymentMethod === 'BANK_TRANSFER' || paymentMethod === 'MOBILE_BANKING');

    const handleSubmit = async () => {
        if (selectedPayrolls.length === 0) return;
        const req: IRecordBatchRequest = {
            batchDate,
            paymentMethod,
            reference: reference.trim() || null,
            payrollIds: selectedPayrolls.map(p => p.id),
            notes: notes.trim() || null,
            reason: reason.trim() || null,
        };
        setSubmitting(true);
        try {
            const batch = await PayrollService.recordBatch(req);
            showSuccess(t('batchPayment.toast.recorded', { count: batch.completedPayments }));
            onSuccess(batch);
            onClose();
        } catch (error) {
            showApiError(error, t('batchPayment.toast.failed'));
        } finally {
            setSubmitting(false);
        }
    };

    const renderPreview = () => {
        if (paymentMethod === 'BANK_TRANSFER') {
            return (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('shared.employee')}</TableCell>
                                <TableCell align="right">{t('shared.netSalary')}</TableCell>
                                <TableCell>{t('shared.bankName')}</TableCell>
                                <TableCell>{t('batchPayment.bankAccount')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {selectedPayrolls.map(p => {
                                const missing = isMissing(p.user?.bankName) || isMissing(p.user?.bankAccountNumber);
                                return (
                                    <TableRow
                                        key={p.id}
                                        sx={missing ? { backgroundColor: 'warning.light' } : undefined}
                                        data-testid={missing ? 'row-missing' : 'row-ok'}
                                    >
                                        <TableCell>{p.employeeName}</TableCell>
                                        <TableCell align="right">{Number(p.netSalary).toFixed(2)}</TableCell>
                                        <TableCell>{p.user?.bankName || <em>{t('shared.missing')}</em>}</TableCell>
                                        <TableCell>{p.user?.bankAccountNumber || <em>{t('shared.missing')}</em>}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        }
        if (paymentMethod === 'MOBILE_BANKING') {
            return (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t('shared.employee')}</TableCell>
                                <TableCell align="right">{t('shared.netSalary')}</TableCell>
                                <TableCell>{t('batchPayment.provider')}</TableCell>
                                <TableCell>{t('shared.account')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {selectedPayrolls.map(p => {
                                const missing = isMissing(p.user?.mobileBankingProvider) || isMissing(p.user?.mobileBankingAccount);
                                return (
                                    <TableRow
                                        key={p.id}
                                        sx={missing ? { backgroundColor: 'warning.light' } : undefined}
                                        data-testid={missing ? 'row-missing' : 'row-ok'}
                                    >
                                        <TableCell>{p.employeeName}</TableCell>
                                        <TableCell align="right">{Number(p.netSalary).toFixed(2)}</TableCell>
                                        <TableCell>{p.user?.mobileBankingProvider || <em>{t('shared.missing')}</em>}</TableCell>
                                        <TableCell>{p.user?.mobileBankingAccount || <em>{t('shared.missing')}</em>}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        }
        return (
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('shared.employee')}</TableCell>
                            <TableCell align="right">{t('shared.netSalary')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {selectedPayrolls.map(p => (
                            <TableRow key={p.id}>
                                <TableCell>{p.employeeName}</TableCell>
                                <TableCell align="right">{Number(p.netSalary).toFixed(2)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{t('batchPayment.title')}</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                            label={t('batchPayment.batchDate')}
                            type="date"
                            value={batchDate}
                            onChange={(e) => setBatchDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            required
                            sx={{ flex: '1 1 200px' }}
                        />
                        <FormControl required sx={{ flex: '1 1 200px' }}>
                            <InputLabel id="batch-method-label">{t('shared.paymentMethod')}</InputLabel>
                            <Select
                                labelId="batch-method-label"
                                label={t('shared.paymentMethod')}
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value as SalaryPaymentMethod)}
                                inputProps={{ 'aria-label': t('shared.paymentMethod') }}
                            >
                                {METHODS.map(m => <MenuItem key={m} value={m}>{t(`paymentMethod.${m}`)}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField
                            label={t('shared.reference')}
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            sx={{ flex: '1 1 200px' }}
                            helperText={t('batchPayment.referenceHelper')}
                        />
                    </Box>

                    <TextField
                        label={t('common:fields.notes')}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        multiline
                        rows={2}
                        fullWidth
                    />
                    <TextField
                        label={t('batchPayment.reasonLabel')}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        fullWidth
                        helperText={t('batchPayment.reasonHelper')}
                    />

                    {bankingUnavailable && (
                        <Alert severity="info">
                            {t('batchPayment.bankingUnavailable')}
                        </Alert>
                    )}
                    {missingInfoCount > 0 && !bankingUnavailable && (
                        <Alert severity="warning">
                            {t(paymentMethod === 'BANK_TRANSFER' ? 'batchPayment.missingBankInfo' : 'batchPayment.missingMobileInfo', {
                                missing: missingInfoCount,
                                total: selectedPayrolls.length,
                            })}
                        </Alert>
                    )}

                    <Typography variant="subtitle2">
                        {t('batchPayment.summary', { count: selectedPayrolls.length, total: totalAmount.toFixed(2) })}
                    </Typography>

                    {renderPreview()}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>{t('common:actions.cancel')}</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={submitting || selectedPayrolls.length === 0}
                >
                    {submitting ? t('batchPayment.recording') : t('batchPayment.submit', { count: selectedPayrolls.length })}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BatchPaymentDialog;
